<script setup>
// Downloads section (Figma nodes 75-3593 / 75-3643). A warm panel with an
// optional heading, an optional check-circle bullet list, and a list of
// downloadable-file rows (title, description, download button + size) separated
// by hairlines. Reuses the shared DownloadButton for the action.
import DownloadButton from '@/components/DownloadButton.vue'

defineProps({
  block: { type: Object, required: true },
  // { heading?, bullets?: [string], items?: [{ title, description, href, label?, size? }] }
})
</script>

<template>
  <section class="downloads">
    <h2 v-if="block.heading" class="heading">{{ block.heading }}</h2>

    <ul v-if="block.bullets && block.bullets.length" class="bullets">
      <li v-for="(text, i) in block.bullets" :key="i">
        <svg class="check" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.15" />
          <path d="m8 12 2.5 2.5L16 9" stroke="currentColor" stroke-width="2"
            stroke-linecap="round" stroke-linejoin="round" />
        </svg>
        <span>{{ text }}</span>
      </li>
    </ul>

    <ul v-if="block.items && block.items.length" class="items">
      <li v-for="(item, i) in block.items" :key="i" class="item">
        <div class="info">
          <h3 class="title">{{ item.title }}</h3>
          <p v-if="item.description" class="description">{{ item.description }}</p>
        </div>
        <DownloadButton :href="item.href" :label="item.label || 'Download'" :size="item.size || ''" />
      </li>
    </ul>
  </section>
</template>

<style lang="scss" scoped>
.downloads {
  padding: $space-6;
  background: var(--color-surface-warm);
  border: 1px solid var(--color-border-decorative);
  @include rounded($radius-lg);
  color: var(--color-ink);

  .heading {
    margin: 0 0 $space-5;
    font-size: $font-size-h2;
    line-height: $lh-heading;
    letter-spacing: $ls-heading;
    font-weight: 500;
  }

  .bullets {
    list-style: none;
    margin: 0 0 $space-5;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: $space-3;

    li {
      display: flex;
      align-items: flex-start;
      gap: $space-3;
      font-size: $font-size-base;
      line-height: $lh-base;
    }

    .check {
      flex-shrink: 0;
      width: 24px;
      height: 24px;
      margin-top: 1px;
      color: var(--color-success);
    }
  }

  .items {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .item {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: $space-4;
    padding: $space-5 0;
    border-top: 1px solid var(--color-border);

    &:first-child { border-top: 0; padding-top: 0; }
    &:last-child  { padding-bottom: 0; }

    .info {
      flex: 1;
      min-width: 220px;
      display: flex;
      flex-direction: column;
      gap: $space-2;
    }

    .title {
      margin: 0;
      font-size: $font-size-h4;
      font-weight: 600;
      line-height: $lh-heading-sm;
    }

    .description {
      margin: 0;
      font-size: $font-size-sm;
      color: var(--color-text);
      line-height: $lh-base;
    }
  }
}
</style>
